import React, { useState, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { isEmpty } from "lodash";

import StepsCard from "../../components/Cards/StepsCard";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import SelectModel from "../../components/PublishModelSteps/SelectModel";
import ManageModel from "../../components/PublishModelSteps/ManageModel";
import ManageSubscriptions from "../../components/PublishModelSteps/ManageSubscriptions";
import LookupsAPI from "../../API/lookups.api";
import {
    BOOLEAN_DROPDOWN,
    CURRENCY,
    MODEL_SCOPE,
    PRODUCT_TYPES,
    SUBSCRIPTION_TYPES,
    TRIAL_DAYS,
} from "../../utils/constants";
import PublishedModelsAPI from "../../API/publishedModels.api";
import PreviewProduct from "../../components/PublishModelSteps/PreviewProduct";
import MultistepCompletionHandler from "../../components/Handlers/MultistepCompletionHandler";
import MainHeader from "../../components/Headers/MainHeader";
import {
    getCountriesDropdown,
    reformatCountry,
} from "../../utils/CountryHelper";
import { ErrorAlert } from "../../utils/Alerts";
import ProductVersionAPI from "../../API/productVersion.api";

const PublishModelForm = ({ isUpdate }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [productId, setProductId] = useState(null);
    const [fetchedProductVersion, setFetchedProductVersion] = useState({});

    //Step 1
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState(null);
    const [parametersDictionary, setParametersDictionary] = useState({});
    const [materialsDictionary, setMaterialsDictionary] = useState({});
    const [removedParameters, setRemovedParameters] = useState([]);
    const [parametersDictrionaryFiltered, setParametersDictrionaryFiltered] =
        useState(false);
    const [selectedParameterVersion, setSelectedParameterVersion] = useState(
        []
    );

    //Step 2
    const [countries, setCountries] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [supportedAccessories, setSupportedAccessories] = useState([]);
    const [scannerTypes, setScannerTypes] = useState([]);
    const [tags, setTags] = useState([]);
    const [productImages, setProductImages] = useState([]);
    const [coverImage, setCoverImage] = useState([]);
    const [productGuide, setProductGuide] = useState({});
    const [isForceUpgradeEnabled, setIsForceUpgradeEnabled] = useState(false);
    const [manageModelForm, setManageModelForm] = useState({
        productName: "",
        materialName: "",
        materialImage: "",
        description: "",
        compatibleWith: [],
        supportedAccessories: [],
        industries: [],
        tags: [],
        productVersion: "",
        whatsNew: [],
        universalModel: "",
        supportedCountries: [],
        images: [],
        instructions: [],
        enforceOutlierDetection: BOOLEAN_DROPDOWN.false,
        forceUpgrade: BOOLEAN_DROPDOWN.false,
    });

    //Step 3
    const [manageSubsciptionsForm, setManageSubsciptionsForm] = useState({
        solo: { value: "Single", booleanValue: true },
        hasTrial: true,
        trialDays: TRIAL_DAYS[7],
        choosenSubscriptionTypes: {},
        monthlySubscription: null,
        annualSubscription: null,
    });

    //Step 5
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const modelId = location.state?.publishedModelId;
    const versionId = location.state?.versionId;

    const STEPS = [
        { number: "1", name: t("publishModelStep1") },
        { number: "2", name: t("publishModelStep2") },
        { number: "3", name: t("publishModelStep3") },
        { number: "4", name: t("publishModelStep4") },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/published-products",
            element: () => (
                <Trans i18nKey="publishedProducts">Published Products</Trans>
            ),
        },
        {
            element: () => getProperTitle(),
        },
    ];

    const finalStepTitles = {
        isSubmitting: t("publishRequestCreationSubmittingTitle"),
        isSubmitted: t("publishRequestSubmitSuccessTitle"),
        default: t("publishRequestSubmitFailTitle"),
    };

    const finalStepContent = {
        isSubmitting: t("publishRequestSubmittingMessage"),
        isSubmitted: t("publishRequestSubmitSuccessMessage"),
        default: (
            <span>
                <Trans i18nKey="publishRequestSubmitFailMessage">
                    Your request could not be uploaded. Please check your
                    internet connection and try again. if you still face any
                    issue please contact us at
                </Trans>
                <b> Support@si-ware.com</b>
            </span>
        ),
    };

    useEffect(() => {
        fetchModelsLookup();
        getCountriesDropdown(setCountries);
        fetchIndustriesLookup();
        fetchSupportedAccessoriesLookup();
        fetchScannerTypes();
        fetchTags();
    }, []);

    useEffect(() => {
        if (!isEmpty(selectedModel))
            setManageModelForm((prevForm) => ({
                ...prevForm,
                materialImage:
                    materialsDictionary[selectedModel.value]?.materialImage ||
                    "",
            }));
    }, [selectedModel]);

    useEffect(() => {
        if (modelId) {
            fetchProductById(modelId);
        }
    }, [location]);

    useEffect(() => {
        const removedParamIds = removedParameters.map((param) => param.value);
        if (
            !isEmpty(removedParamIds) &&
            !isEmpty(parametersDictionary) &&
            modelId &&
            !parametersDictrionaryFiltered &&
            selectedModel
        ) {
            setParametersDictionary((prevDict) => {
                const filteredMap = prevDict[selectedModel?.value]?.filter(
                    (param) => !removedParamIds.includes(param.value)
                );
                return { ...prevDict, [selectedModel?.value]: filteredMap };
            });
            setParametersDictrionaryFiltered(true);
        }
    }, [parametersDictionary, selectedModel, removedParameters]);

    const nextStep = () => {
        setCurrentStep((prev) => prev + 1);
    };

    const prevStep = () => {
        setCurrentStep((prev) => prev - 1);
    };

    const fetchProductById = async (id) => {
        const result = await PublishedModelsAPI.getPublishedProductById(
            id,
            versionId
        );
        if (isEmpty(result)) {
            ErrorAlert(t("loadProductVersionFailMessage"));
            history.push("/published-products");
        } else {
            mapProduct(result);
            setFetchedProductVersion(result);
        }
    };

    const getPriceFromPlan = (plans = [], type, currency) => {
        return (
            plans?.find(
                (plan) =>
                    plan.durationType === type &&
                    plan.currency?.abbreviation === currency
            )?.price || null
        );
    };

    const mapProduct = (product) => {
        if (product) {
            const subscriptionPlans = product.subscriptionPlans;

            setProductId(product.id);

            setSelectedModel({
                label: product.modelName,
                value: product.modelId,
            });

            setRemovedParameters((prevState) => {
                return [
                    ...prevState,
                    ...product.parameters?.map((parameter) => ({
                        label: parameter.parameterName?.split("-")[0],
                        value: parameter.parameterId,
                    })),
                ];
            });

            setSelectedParameterVersion(
                product.parameters?.map((parameter) => ({
                    label: parameter.parameterName,
                    value: parameter.modelParameterVersionId,
                    hidden: parameter.hidden,
                    parameterId: parameter.parameterId,
                }))
            );

            setManageModelForm((prevForm) => ({
                ...prevForm,
                ...(isUpdate && { productVersion: product.version }),
                ...(isUpdate && { whatsNew: product.whatsNew }),
                productName: product.productName,
                materialName: product.materialNames?.[0] || "",
                materialImage: product.materialImage,
                description: product.description,
                compatibleWith: product.compatibleWith,
                supportedAccessories: product.supportedAccessories,
                industries: product.industries,
                tags: product.tags,
                instructions: product.instructions,
                universalModel: {
                    value: product.universalModel
                        ? MODEL_SCOPE.global
                        : MODEL_SCOPE.local,
                    booleanValue: product.universalModel,
                },
                supportedCountries: product.supportedCountries?.map((c) =>
                    reformatCountry(c)
                ),
                forceUpgrade:
                    BOOLEAN_DROPDOWN[product.forceUpgrade] ||
                    BOOLEAN_DROPDOWN.false,
                enforceOutlierDetection:
                    BOOLEAN_DROPDOWN[product.enforceOutlierDetection] ||
                    BOOLEAN_DROPDOWN.false,
            }));

            setCoverImage(product.coverImage ? [product.coverImage] : []);

            setProductImages(product.images);

            setManageSubsciptionsForm((prevForm) => ({
                ...prevForm,
                solo: {
                    value: product.solo ? "Single" : "Bundle",
                    booleanValue: product.solo,
                },
                hasTrial: product.hasTrial,
                trialDays: TRIAL_DAYS[product.trialDays],
                isMonthlyRequired: product.isMonthlyRequired,
                isAnnualRequired: product.isAnnualRequired,
                choosenSubscriptionTypes: {
                    annualSubscription: subscriptionPlans?.some(
                        (plan) =>
                            plan.durationType === SUBSCRIPTION_TYPES.annual
                    ),
                    monthlySubscription: subscriptionPlans?.some(
                        (plan) =>
                            plan.durationType === SUBSCRIPTION_TYPES.monthly
                    ),
                },
                annualSubscriptionDollar: getPriceFromPlan(
                    subscriptionPlans,
                    SUBSCRIPTION_TYPES.annual,
                    CURRENCY.dollar
                ),

                annualSubscriptionEuro: getPriceFromPlan(
                    subscriptionPlans,
                    SUBSCRIPTION_TYPES.annual,
                    CURRENCY.euro
                ),

                monthlySubscriptionDollar: getPriceFromPlan(
                    subscriptionPlans,
                    SUBSCRIPTION_TYPES.monthly,
                    CURRENCY.dollar
                ),

                monthlySubscriptionEuro: getPriceFromPlan(
                    subscriptionPlans,
                    SUBSCRIPTION_TYPES.monthly,
                    CURRENCY.euro
                ),
            }));
        }
    };

    const fetchModelsLookup = async () => {
        const result = (await LookupsAPI.getModels(false)) || [];
        const parametersResult = {};
        const modelsMaterial = {};
        setModels(
            result.map((model) => {
                modelsMaterial[model.id] = {
                    materialImage: model.materialImage,
                    materialName: model.materialName,
                    materialId: model.materialId,
                };
                parametersResult[model.id] = model.modelParameters.map(
                    (modelParameter) => {
                        return {
                            label: modelParameter.parameterName,
                            value: modelParameter.id,
                            isDisabled: !modelParameter.hasVersions,
                        };
                    }
                );
                return { label: model.modelName, value: model.id };
            })
        );
        setMaterialsDictionary(modelsMaterial);
        setParametersDictionary(parametersResult);
    };

    const fetchSupportedAccessoriesLookup = async () => {
        const result = await LookupsAPI.getSupportedAccessories();
        setSupportedAccessories(result);
    };

    const fetchIndustriesLookup = async () => {
        const result = await LookupsAPI.getIndustries();
        setIndustries(result);
    };

    const fetchScannerTypes = async () => {
        const result = await LookupsAPI.getScannerTypes();
        setScannerTypes(result);
    };

    const fetchTags = async () => {
        const result = await LookupsAPI.getTags();
        setTags(result);
    };

    const getIdMappedArray = (propName) => {
        const map = {};
        fetchedProductVersion[propName]?.forEach((prop) => {
            map[prop.id || prop.value || prop] = prop;
        });
        return map;
    };

    const mapIsDeletedObjects = (dict) => {
        return (
            dict &&
            Object.values(dict).map((val) => ({
                id: val.id || val.value,
                isDeleted: true,
            }))
        );
    };

    const getUpdateObjectValues = (arr, propName, extraProps = () => {}) => {
        const map = getIdMappedArray(propName);
        const mappedArr = arr.map((item) => {
            const itemId = item.id || item.value;
            const alreadyExists = !!map[itemId];
            delete map[itemId];
            return { id: itemId, ...extraProps(item, alreadyExists) };
        });
        const deletedItems = mapIsDeletedObjects(map) || [];

        return [...mappedArr, ...deletedItems];
    };

    const getObjectValues = (_object) => {
        return _object && _object.map((obj) => obj.value);
    };

    const sanitizeCreatableSelectPayload = (arr, dict) => {
        return arr.map((record) => {
            const recordName = record.name || record.label;
            const recordId = record.id || record.value;
            dict && delete dict[recordId];
            if (recordName && record.isCreated) {
                return {
                    name: recordName,
                    isCreated: record.isCreated,
                };
            } else if (recordId) {
                return {
                    id: recordId,
                };
            }
        });
    };

    const sanitizeUniqueCreatableSelect = (obj, propName) => {
        const map = getIdMappedArray(propName);
        const arr = obj[propName];
        const sanitizedArr = sanitizeCreatableSelectPayload(arr, map);
        const addedDeleteTags = mapIsDeletedObjects(map) || [];

        return [...sanitizedArr, ...addedDeleteTags];
    };

    const reformatFormData = () => {
        let reformattedData = {
            modelId: selectedModel.value,
            modelParameterVersions: selectedParameterVersion.map(
                (parameterVersion) => ({
                    modelParameterVersionId: parameterVersion.value,
                    hidden: parameterVersion.hidden,
                })
            ),
            ...manageModelForm,
            industries: sanitizeCreatableSelectPayload(
                manageModelForm.industries
            ),
            tags: sanitizeCreatableSelectPayload(manageModelForm.tags),
            coverImage: manageModelForm.coverImage[0],
            productGuideURI: manageModelForm.productGuideURI,
            compatibleWith: getObjectValues(manageModelForm.compatibleWith),
            supportedAccessories: getObjectValues(
                manageModelForm.supportedAccessories
            ),
            universalModel: manageModelForm.universalModel?.booleanValue,
            supportedCountries: manageModelForm.universalModel?.booleanValue
                ? []
                : getObjectValues(manageModelForm.supportedCountries),
            type: PRODUCT_TYPES.model,
            solo: manageSubsciptionsForm.solo.booleanValue,
            hasTrial: manageSubsciptionsForm.hasTrial,
            ...(manageSubsciptionsForm.hasTrial && {
                trialDays: manageSubsciptionsForm.trialDays?.value,
            }),
            subscriptionPlans: manageSubsciptionsForm.solo.booleanValue
                ? []
                : undefined,
            forceUpgrade:
                manageModelForm.forceUpgrade?.booleanValue ||
                BOOLEAN_DROPDOWN.false.value,
            enforceOutlierDetection:
                manageModelForm.enforceOutlierDetection?.booleanValue ||
                BOOLEAN_DROPDOWN.false.value,
        };

        if (modelId) {
            reformattedData = { ...reformattedData, productId };
        }

        if (reformattedData?.subscriptionPlans) {
            if (
                manageSubsciptionsForm?.choosenSubscriptionTypes
                    ?.monthlySubscription
            ) {
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.monthlySubscriptionDollar,
                    durationType: SUBSCRIPTION_TYPES.monthly,
                    currencyId: 1,
                });

                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.monthlySubscriptionEuro,
                    durationType: SUBSCRIPTION_TYPES.monthly,
                    currencyId: 2,
                });
            }
            if (
                manageSubsciptionsForm?.choosenSubscriptionTypes
                    ?.annualSubscription
            ) {
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.annualSubscriptionDollar,
                    durationType: SUBSCRIPTION_TYPES.annual,
                    currencyId: 1,
                });
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.annualSubscriptionEuro,
                    durationType: SUBSCRIPTION_TYPES.annual,
                    currencyId: 2,
                });
            }
        }
        return reformattedData;
    };

    const getUpdatePayload = () => {
        let reformattedData = {
            id: fetchedProductVersion.productVersionId,
            modelParameterVersions: selectedParameterVersion.map(
                (parameterVersion) => ({
                    modelParameterVersionId: parameterVersion.value,
                    hidden: parameterVersion.hidden,
                })
            ),
            ...manageModelForm,
            images: getUpdateObjectValues(
                productImages,
                "images",
                (item, exists) =>
                    exists ? {} : { image: item.preview, isCreated: true }
            ),
            industries: sanitizeUniqueCreatableSelect(
                manageModelForm,
                "industries"
            ),
            tags: sanitizeUniqueCreatableSelect(manageModelForm, "tags"),
            coverImage: manageModelForm.coverImage[0],
            productGuideURI: manageModelForm.productGuideURI,
            scannerTypes: getUpdateObjectValues(
                manageModelForm.compatibleWith,
                "compatibleWith"
            ),
            version: manageModelForm.productVersion,
            supportedAccessories: getUpdateObjectValues(
                manageModelForm.supportedAccessories,
                "supportedAccessories"
            ),
            universalModel: manageModelForm.universalModel?.booleanValue,
            supportedCountries: manageModelForm.universalModel?.booleanValue
                ? []
                : getUpdateObjectValues(
                      manageModelForm.supportedCountries,
                      "supportedCountries"
                  ),
            type: PRODUCT_TYPES.model,
            solo: manageSubsciptionsForm.solo.booleanValue,
            hasTrial: manageSubsciptionsForm.solo.booleanValue
                ? manageSubsciptionsForm.hasTrial
                : undefined,
            trialDays: manageSubsciptionsForm.solo.booleanValue
                ? manageSubsciptionsForm?.trialDays?.value
                : undefined,

            subscriptionPlans: manageSubsciptionsForm.solo.booleanValue
                ? []
                : undefined,
            forceUpgrade:
                manageModelForm.forceUpgrade?.booleanValue ||
                BOOLEAN_DROPDOWN.false.value,
            enforceOutlierDetection:
                manageModelForm.enforceOutlierDetection?.booleanValue ||
                BOOLEAN_DROPDOWN.false.value,
        };

        if (reformattedData?.subscriptionPlans) {
            if (
                manageSubsciptionsForm?.choosenSubscriptionTypes
                    ?.monthlySubscription
            ) {
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.monthlySubscriptionDollar,
                    durationType: SUBSCRIPTION_TYPES.monthly,
                    currencyId: 1,
                });

                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.monthlySubscriptionEuro,
                    durationType: SUBSCRIPTION_TYPES.monthly,
                    currencyId: 2,
                });
            }
            if (
                manageSubsciptionsForm?.choosenSubscriptionTypes
                    ?.annualSubscription
            ) {
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.annualSubscriptionDollar,
                    durationType: SUBSCRIPTION_TYPES.annual,
                    currencyId: 1,
                });
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.annualSubscriptionEuro,
                    durationType: SUBSCRIPTION_TYPES.annual,
                    currencyId: 2,
                });
            }
        }

        delete reformattedData.compatibleWith;
        delete reformattedData.productVersion;

        return reformattedData;
    };

    const getProperSubmitAction = () => {
        if (!modelId) return publishModel;

        return isUpdate ? updateModelVersion : createNewVersion;
    };

    const getProperTitle = () => {
        if (!modelId) return t("publishProduct");

        return isUpdate ? t("editVersion") : t("newVersion");
    };

    const publishModel = async () => {
        setIsSubmitting(true);
        const result = await PublishedModelsAPI.publishModel(
            reformatFormData()
        );
        if (result?.inserted) {
            setIsSubmitted(true);
        }
        setIsSubmitting(false);
    };

    const createNewVersion = async () => {
        setIsSubmitting(true);
        const result = await ProductVersionAPI.createProductVersion(
            reformatFormData()
        );
        if (result?.inserted) {
            setIsSubmitted(true);
        }
        setIsSubmitting(false);
    };

    const updateModelVersion = async () => {
        setIsSubmitting(true);
        const result = await ProductVersionAPI.updateProductVersion(
            getUpdatePayload()
        );
        if (result?.modified) {
            setIsSubmitted(true);
        }
        setIsSubmitting(false);
    };

    const renderForm = () => {
        switch (currentStep) {
            case 1:
                return (
                    <SelectModel
                        nextStepButtonText={t("publishModelStep2")}
                        nextStep={nextStep}
                        models={models}
                        parametersDictionary={parametersDictionary}
                        setParametersDictionary={setParametersDictionary}
                        removedParameters={removedParameters}
                        setRemovedParameters={setRemovedParameters}
                        selectedModel={selectedModel}
                        setSelectedModel={setSelectedModel}
                        selectedParameterVersion={selectedParameterVersion}
                        setSelectedParameterVersion={
                            setSelectedParameterVersion
                        }
                        product={fetchedProductVersion}
                        setIsForceUpgradeEnabled={setIsForceUpgradeEnabled}
                        isNewVersion={modelId}
                    />
                );
            case 2:
                return (
                    <ManageModel
                        nextStepButtonText={t("publishModelStep3")}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        selectedModelMaterial={
                            materialsDictionary[selectedModel.value]
                        }
                        manageModelForm={manageModelForm}
                        setManageModelForm={setManageModelForm}
                        selectedModel={selectedModel}
                        selectedParameterVersion={selectedParameterVersion}
                        supportedAccessories={supportedAccessories}
                        countries={countries}
                        industries={industries}
                        scannerTypes={scannerTypes}
                        tags={tags}
                        productImages={productImages}
                        setProductImages={setProductImages}
                        coverImage={coverImage}
                        setCoverImage={setCoverImage}
                        productGuide={productGuide}
                        setProductGuide={setProductGuide}
                        isNewVersion={modelId}
                        productId={productId}
                        product={fetchedProductVersion}
                        isUpdate={isUpdate}
                        isForceUpgradeEnabled={isForceUpgradeEnabled}
                    />
                );
            case 3:
                return (
                    <ManageSubscriptions
                        nextStepButtonText={t("publishModelStep4")}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        manageSubsciptionsForm={manageSubsciptionsForm}
                        setManageSubsciptionsForm={setManageSubsciptionsForm}
                        isNewVersion={modelId}
                    />
                );
            case 4:
                return (
                    <PreviewProduct
                        product={manageModelForm}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        publishModel={getProperSubmitAction()}
                        selectedParameterVersion={selectedParameterVersion}
                        manageSubsciptionsForm={manageSubsciptionsForm}
                        isNewVersion={modelId}
                    />
                );
            case 5:
                return (
                    <MultistepCompletionHandler
                        isSubmitting={isSubmitting}
                        isSubmitted={isSubmitted}
                        prevStep={prevStep}
                        titles={finalStepTitles}
                        content={finalStepContent}
                        successRedirect={() => history.goBack()}
                    />
                );
            default:
                return <div></div>;
        }
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <MainHeader header={getProperTitle()} />
            <StepsCard
                steps={STEPS}
                activeNumber={currentStep}
                className="mb-4"
            />
            {renderForm()}
        </>
    );
};

export default PublishModelForm;
